import { Job, JobStatus, SupabaseJob } from '@applicator/shared'
import { formatSalaryForDisplay } from '@applicator/shared'
import { useRef } from 'react'

import { extractKeywords } from '../keywords'
import { renderSavedAtAndUrl, renderRelevancySection, renderDateChipsSection } from '../renderers'

// CSS constants to avoid duplication
const SLIDE_OUT_TRANSFORM = 'translateX(100px)'
const BORDER_BOX_SIZING = 'border-box'

interface JobDetailContentProps {
  currentJob: Job
  detailContentRef: React.RefObject<HTMLDivElement>
  jobDetailScrollRef: React.RefObject<HTMLDivElement>
  isInitialMount: boolean
  isReturning: boolean
  isTransitioning: boolean
  searchQueryId: string | null
  navigate: (path: string) => void
  setIsReturning: React.Dispatch<React.SetStateAction<boolean>>
  leftColumnRef: React.RefObject<HTMLDivElement>
  editingPosition: boolean
  editingCompany: boolean
  editingSalary: boolean
  positionInputValue: string
  companyInputValue: string
  salaryInputValue: string
  positionInputRef: React.RefObject<HTMLInputElement>
  companyInputRef: React.RefObject<HTMLInputElement>
  salaryInputRef: React.RefObject<HTMLInputElement>
  savedAtRef: React.RefObject<HTMLSpanElement>
  setSavedAtTooltipPos: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>
  setSavedAtTooltipText: React.Dispatch<React.SetStateAction<string>>
  setShowSavedAtTooltip: React.Dispatch<React.SetStateAction<boolean>>
  hoveredStar: number | null
  setHoveredStar: React.Dispatch<React.SetStateAction<number | null>>
  supabaseJobs: SupabaseJob[]
  showRelevanceReasoning: boolean
  setShowRelevanceReasoning: React.Dispatch<React.SetStateAction<boolean>>
  userRules: { follow_up_days_after_applied?: number | null; deadline_days_after_saved?: number | null }
  handlePositionEditStartWrapper: () => void
  handlePositionSaveWrapper: () => void
  handlePositionKeyDownWrapper: (e: React.KeyboardEvent) => void
  handleCompanyEditStartWrapper: () => void
  handleCompanySave: () => void
  handleCompanyKeyDown: (e: React.KeyboardEvent) => void
  handleSalaryEditStartWrapper: () => void
  handleSalarySaveWrapper: () => void
  handleSalaryKeyDownWrapper: (e: React.KeyboardEvent) => void
  handleSalaryInputChangeWrapper: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleExcitementChangeWrapper: (value: number) => void
  handleDateChipsUpdateWrapper: (updatedDates: Record<string, unknown>) => Promise<void>
  handleAddToJobs: () => Promise<void>
  handleRejectRelevant: () => Promise<void>
  handleStatusChange: (newStatus: JobStatus) => Promise<void>
  handleDeleteJob: () => Promise<void>
  closeJobDropdownOpen: boolean
  setCloseJobDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>
  closeJobButtonRef: React.RefObject<HTMLButtonElement>
  closeJobDropdownRef: React.RefObject<HTMLDivElement>
  pipelineStatuses: JobStatus[]
  closeJobStatuses: JobStatus[]
  setPositionInputValue: React.Dispatch<React.SetStateAction<string>>
  setCompanyInputValue: React.Dispatch<React.SetStateAction<string>>
}

export function JobDetailContent({
  currentJob,
  detailContentRef,
  jobDetailScrollRef,
  isInitialMount,
  isReturning,
  isTransitioning,
  searchQueryId,
  navigate,
  setIsReturning,
  leftColumnRef,
  editingPosition,
  editingCompany,
  editingSalary,
  positionInputValue,
  companyInputValue,
  salaryInputValue,
  positionInputRef,
  companyInputRef,
  salaryInputRef,
  savedAtRef,
  setSavedAtTooltipPos,
  setSavedAtTooltipText,
  setShowSavedAtTooltip,
  hoveredStar,
  setHoveredStar,
  supabaseJobs,
  showRelevanceReasoning,
  setShowRelevanceReasoning,
  userRules,
  handlePositionEditStartWrapper,
  handlePositionSaveWrapper,
  handlePositionKeyDownWrapper,
  handleCompanyEditStartWrapper,
  handleCompanySave,
  handleCompanyKeyDown,
  handleSalaryEditStartWrapper,
  handleSalarySaveWrapper,
  handleSalaryKeyDownWrapper,
  handleSalaryInputChangeWrapper,
  handleExcitementChangeWrapper,
  handleDateChipsUpdateWrapper,
  handleAddToJobs,
  handleRejectRelevant,
  handleStatusChange,
  handleDeleteJob,
  closeJobDropdownOpen,
  setCloseJobDropdownOpen,
  closeJobButtonRef,
  closeJobDropdownRef,
  pipelineStatuses,
  closeJobStatuses,
  setPositionInputValue,
  setCompanyInputValue,
}: JobDetailContentProps) {
  // Use ref to store timeout ID for clearing hover state
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const renderStars = () => {
    if (!currentJob) return null
    const count = currentJob.excitement || 0
    // When hovering, show all stars up to and including the hovered star
    // hoveredStar is 0-based index (0, 1, 2, 3, 4), so we add 1 to get the count
    const displayCount = hoveredStar !== null ? hoveredStar + 1 : count

    return (
      <div 
        className="flex gap-0.5"
        onMouseLeave={() => {
          // Clear any pending timeout
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current)
            hoverTimeoutRef.current = null
          }
          setHoveredStar(null)
        }}
      >
        {[...Array(5)].map((_, i) => {
          // Determine if this star should be highlighted
          // When hovering, highlight all stars from 0 to hoveredStar (inclusive)
          // For example, if hoveredStar=2 (third star), highlight stars at indices 0, 1, 2
          const isHighlighted = hoveredStar !== null 
            ? i <= hoveredStar  // When hovering: highlight all stars up to and including the hovered star
            : i < count  // When not hovering: highlight based on current excitement value
          
          return (
            <span
              key={i}
              role="button"
              tabIndex={0}
              className={`text-base cursor-pointer transition-colors ${
                isHighlighted ? 'text-yellow-400' : 'text-border-strong'
              }`}
              onMouseEnter={() => {
                // Clear any pending timeout when entering a star
                if (hoverTimeoutRef.current) {
                  clearTimeout(hoverTimeoutRef.current)
                  hoverTimeoutRef.current = null
                }
                // Set hoveredStar to the index of the star being hovered
                // This will cause all stars from 0 to i (inclusive) to be highlighted
                setHoveredStar(i)
              }}
              onClick={() => {
                handleExcitementChangeWrapper(i + 1)
                setHoveredStar(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleExcitementChangeWrapper(i + 1)
                  setHoveredStar(null)
                }
              }}
            >
              {isHighlighted ? '★' : '☆'}
            </span>
          )
        })}
      </div>
    )
  }

  return (
    <div
      ref={(node) => {
        if (node) {
          // Use Object.assign to avoid TypeScript readonly error
          Object.assign(detailContentRef, { current: node })
          Object.assign(jobDetailScrollRef, { current: node })
        }
      }}
      className="flex-1 transition-all duration-500 ease-out job-detail-scrollable"
      style={{
        opacity: isInitialMount ? 0 : isReturning ? 0 : 1,
        transform: isInitialMount ? SLIDE_OUT_TRANSFORM : isReturning ? SLIDE_OUT_TRANSFORM : 'translateX(0)',
        willChange: isTransitioning || isInitialMount || isReturning ? 'opacity, transform' : 'auto',
        height: '100%',
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div className="p-6 flex flex-col">
        {/* Back button */}
        <button
          onClick={() => {
            setIsReturning(true)
            
            // Плавно скрываем детальный контент
            if (detailContentRef.current) {
              detailContentRef.current.style.opacity = '0'
              detailContentRef.current.style.transform = 'translateX(100px)'
            }
            
            // Разворачиваем левую колонку на весь экран
            if (leftColumnRef.current) {
              leftColumnRef.current.style.width = '100%'
            }
            
            // После завершения анимации выполняем навигацию
            setTimeout(() => {
              const backUrl = searchQueryId 
                ? `/?searchQueryId=${searchQueryId}`
                : '/'
              navigate(backUrl)
            }, 500) // Длительность анимации
          }}
          className="mb-2 flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors"
        >
          <span>&lt;</span>
          <span>Back to Jobs</span>
        </button>

        {/* Job Header - Compact */}
        <div className="mb-3">
          {/* Row 1: Job Title and Salary */}
          <div className="flex items-center justify-between gap-4 mb-1">
            <div className="flex-1 min-w-0">
              {editingPosition ? (
                <input
                  ref={positionInputRef}
                  type="text"
                  value={positionInputValue}
                  onChange={(e) => setPositionInputValue(e.target.value)}
                  onBlur={handlePositionSaveWrapper}
                  onKeyDown={handlePositionKeyDownWrapper}
                  className="text-xl font-bold text-text-primary mb-0 w-full px-2 py-1 border-b border-border-strong focus:outline-none focus:border-interactive bg-transparent leading-tight"
                  style={{
                    boxSizing: BORDER_BOX_SIZING,
                  }}
                />
              ) : (
                <h1 
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                  role="button"
                  tabIndex={0}
                  className="text-xl font-bold text-text-primary mb-0 cursor-pointer hover:bg-surface-muted rounded px-2 py-1 -mx-2 transition-colors leading-tight"
                  onClick={handlePositionEditStartWrapper}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handlePositionEditStartWrapper()
                    }
                  }}
                  title="Click to edit"
                >
                  {currentJob.position}
                </h1>
              )}
            </div>
            <div className="flex-shrink-0 flex items-center gap-2">
              <span className="text-xs font-medium text-text-muted uppercase tracking-wide">SALARY</span>
              {editingSalary ? (
                <input
                  ref={salaryInputRef}
                  type="text"
                  value={salaryInputValue}
                  onChange={handleSalaryInputChangeWrapper}
                  onBlur={handleSalarySaveWrapper}
                  onKeyDown={handleSalaryKeyDownWrapper}
                  className="text-sm text-text-primary font-medium w-20 px-2 py-1 border-b border-border-strong focus:outline-none focus:border-interactive bg-transparent text-right"
                  style={{
                    boxSizing: BORDER_BOX_SIZING,
                  }}
                />
              ) : (
                <div 
                  role="button"
                  tabIndex={0}
                  className="text-sm text-text-primary font-medium cursor-pointer hover:bg-surface-muted rounded px-2 py-1 -mx-2 transition-colors"
                  onClick={handleSalaryEditStartWrapper}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleSalaryEditStartWrapper()
                    }
                  }}
                  title="Click to edit"
                >
                  {formatSalaryForDisplay(currentJob.salary)}
                </div>
              )}
            </div>
          </div>
          
          {/* Row 2: Company, Location, Saved time, Stars - all in one line */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-baseline gap-2 flex-shrink-0">
              {editingCompany ? (
                <input
                  ref={companyInputRef}
                  type="text"
                  value={companyInputValue}
                  onChange={(e) => setCompanyInputValue(e.target.value)}
                  onBlur={handleCompanySave}
                  onKeyDown={handleCompanyKeyDown}
                  className="text-base font-semibold text-text-primary inline-block min-w-[100px] max-w-[200px] px-2 py-1 border-b border-border-strong focus:outline-none focus:border-interactive bg-transparent"
                  style={{
                    boxSizing: BORDER_BOX_SIZING,
                  }}
                />
              ) : (
                <p 
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                  role="button"
                  tabIndex={0}
                  className="text-base font-semibold text-text-primary cursor-pointer hover:bg-surface-muted rounded px-2 py-1 -mx-2 transition-colors"
                  onClick={handleCompanyEditStartWrapper}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleCompanyEditStartWrapper()
                    }
                  }}
                  title="Click to edit"
                >
                  {currentJob.company}
                </p>
              )}
              <span className="text-sm font-normal text-text-secondary whitespace-nowrap">— {currentJob.location}</span>
            </div>
            {renderSavedAtAndUrl(currentJob, savedAtRef, setSavedAtTooltipPos, setSavedAtTooltipText, setShowSavedAtTooltip)}
            <div className="flex-shrink-0 ml-auto">
              {renderStars()}
            </div>
          </div>
        </div>

        {/* Main content area */}
        <div className="mb-3">
          {/* Status Pipeline - only show when NOT viewing search query results */}
          {!searchQueryId && (
            <div className="mb-3">
              <div className="flex items-stretch w-full">
                {pipelineStatuses.map((status, index) => {
                  const isActive = currentJob.status === status
                  const currentIndex = pipelineStatuses.indexOf(currentJob.status)
                  const isPast = currentIndex !== -1 && currentIndex > index
                  
                  return (
                    <button
                      key={status}
                      onClick={() => handleStatusChange(status)}
                      className={`flex-1 py-2.5 text-sm font-medium transition-colors duration-200 border ${
                        isActive
                          ? 'bg-brand-primary text-text-inverse border-brand-primary'
                          : isPast
                          ? 'bg-surface-muted text-text-secondary border-border-strong hover:bg-surface-muted'
                          : 'bg-surface-default text-text-secondary border-border-strong hover:bg-surface-subtle hover:border-border-strong'
                      } ${
                        index === 0 ? 'rounded-l-md' : 'border-l-0'
                      } ${
                        index === pipelineStatuses.length - 1 ? 'rounded-r-md' : ''
                      }`}
                    >
                      {status}
                    </button>
                  )
                })}
                {/* Close Job button with dropdown */}
                <div className="relative">
                  <button
                    ref={closeJobButtonRef}
                    data-close-job-button
                    onClick={() => setCloseJobDropdownOpen(!closeJobDropdownOpen)}
                    className={`h-full px-4 py-2.5 text-sm font-medium rounded-r-md whitespace-nowrap transition-colors duration-200 flex items-center gap-1.5 border ${
                      currentJob.status === 'Archived' || currentJob.status === 'I Withdrew' || currentJob.status === 'Not Selected' || currentJob.status === 'No Response'
                        ? 'bg-brand-primary text-text-inverse border-brand-primary'
                        : 'bg-surface-default text-text-secondary border-border-strong border-l-0 hover:bg-surface-subtle hover:border-border-strong'
                    }`}
                  >
                    Close Job
                    <svg
                      className={`w-3.5 h-3.5 transition-transform duration-200 ${closeJobDropdownOpen ? 'transform rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {closeJobDropdownOpen && (
                    <div
                      ref={closeJobDropdownRef}
                      className="absolute top-full left-0 mt-1 bg-surface-default border border-border-default rounded-md shadow-lg z-[9999] w-48 overflow-hidden"
                    >
                      {closeJobStatuses.map((status) => (
                        <div
                          key={status}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleStatusChange(status)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              handleStatusChange(status)
                            }
                          }}
                          className={`px-3 py-2 text-sm cursor-pointer transition-colors duration-150 ${
                            currentJob.status === status
                              ? 'bg-brand-primary-subtle text-brand-primary font-medium'
                              : 'text-text-secondary hover:bg-surface-subtle'
                          }`}
                        >
                          {status === 'No Response' ? 'No Response 👻' : status}
                        </div>
                      ))}
                      <div className="border-t border-border-default"></div>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={handleDeleteJob}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            handleDeleteJob()
                          }
                        }}
                        className="px-3 py-2 text-sm cursor-pointer text-danger hover:bg-danger-subtle transition-colors duration-150"
                      >
                        Delete Job
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Compact block: Start Applying + Relevancy (only for search queries) */}
          {renderRelevancySection(searchQueryId, currentJob, supabaseJobs, showRelevanceReasoning, setShowRelevanceReasoning, handleAddToJobs, handleRejectRelevant)}

          {/* Compact Date Chips in horizontal row */}
          {renderDateChipsSection(currentJob, supabaseJobs, userRules, handleDateChipsUpdateWrapper)}
        </div>

        {/* Job Description and Keywords */}
        <div className="grid grid-cols-2 gap-2 flex-1 min-h-0">
          {/* Left: Job Description */}
          <div className="border border-border-default rounded px-2 py-1.5 bg-surface-default flex flex-col min-h-0">
            <div className="text-xs font-medium text-text-muted uppercase tracking-wide mb-1.5 block leading-tight flex-shrink-0">
              JOB DESCRIPTION
            </div>
            <div className="text-sm text-text-primary leading-relaxed whitespace-pre-wrap flex-1 overflow-y-auto min-h-0">
              {(() => {
                if (!currentJob?.description) return 'No description available.'
                // Remove "Original URL: ..." line from description
                const cleanedDescription = currentJob?.description?.replace(/^Original URL:\s*.+?(?:\n\n|$)/, '')?.trim() || ''
                return cleanedDescription || 'No description available.'
              })()}
            </div>
          </div>

          {/* Right: Keywords */}
          <div className="border border-border-default rounded px-2 py-1.5 bg-surface-default flex flex-col min-h-0">
            <div className="text-xs font-medium text-text-muted uppercase tracking-wide mb-1.5 block leading-tight flex-shrink-0">
              KEYWORDS
            </div>
            <div className="flex flex-wrap gap-1.5 flex-1 overflow-y-auto content-start min-h-0">
              {(() => {
                if (!currentJob?.description) {
                  return <span className="text-sm text-text-muted">No keywords available.</span>
                }
                const keywords = extractKeywords(currentJob?.description)
                if (keywords.length === 0) {
                  return <span className="text-sm text-text-muted">No keywords available.</span>
                }
                return keywords.map(({ word, count }) => (
                  <span
                    key={word}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface-muted text-text-primary"
                  >
                    {word} {count}
                  </span>
                ))
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
